// Viewerens statusplads i HUD-stakken (instruktion -> kort bekræftelse -> STATUS, se HudSkin.js layoutHudStack).
//
// Ren PRÆSENTER: den tegner det snapshot, værten (frontendens beskedkerne) giver den, og melder brugerhandlinger tilbage; den har ingen
// regler for prioritet, kø, dubletter eller levetid (de ligger ét sted: kernen i frontenden, utils/status-core.js, kontrakt i frontendens
// VIEWER-UI-OVERLAYS.md 1.8). Findes der ingen vært (standalone Potree), bruges kun den ældre `viewer.postMessage`-liste, som denne
// plads også huser, så den ikke ligger nederst til venstre oven på værtens knapper.
//
// Tekst sættes altid med textContent (aldrig HTML). Fremdriftstal (detail) er aria-hidden: skærmlæseren får titlen og afslutningen.
import {ensureHudStyle, registerHudSlot, unregisterHudSlot, layoutHudStack} from "./HudSkin.js";

const GLYPH = {success: "✔", warning: "⚠", error: "✖", info: "ℹ"};

export class HudStatus{
	constructor(viewer){
		this.viewer = viewer;
		this.handler = null;
		this.snapshot = null;
		this.labels = {};
		this.frame = null;

		ensureHudStyle();
		this.el = document.createElement("div");
		this.el.className = "pvh pvh--top pvh-status";
		this.el.style.cssText = "z-index: 10000; display: none;";
		this.rowsEl = document.createElement("div");
		this.rowsEl.className = "pvh-status-rows";
		this.rowsEl.setAttribute("role", "region");
		this.el.appendChild(this.rowsEl);
		document.body.appendChild(this.el);
		registerHudSlot("status", this.el);

		// den ældre beskedliste (viewer.postMessage) bor i samme plads - flyttet ind, så den følger HUD-stakken
		this.legacy = viewer && viewer.elMessages && viewer.elMessages[0] ? viewer.elMessages[0] : null;
		if(this.legacy){
			this.legacy.style.cssText = "position: static; z-index: auto; left: auto; bottom: auto;";
			this.el.appendChild(this.legacy);
			// jQuery-animationer og tekstopdateringer ændrer listen uden at melde det: kun den ændres overvåges (ikke pladsens egen stil)
			this.observer = new MutationObserver(() => this._scheduleRefresh());
			this.observer.observe(this.legacy, {childList: true, subtree: true, characterData: true, attributes: true});
		}
		this._onResize = () => this._scheduleRefresh();
		window.addEventListener("resize", this._onResize);
	}

	setHandler(fn){
		this.handler = typeof fn === "function" ? fn : null;
	}

	// snap: {tasks, hidden, problem, notice} fra kernen; labels: {region, hide, close, hidden(count)} (oversat af værten)
	setSnapshot(snap, labels){
		this.snapshot = snap || null;
		this.labels = labels || this.labels;
		this._render();
	}

	_send(id, op){
		if(this.handler) this.handler(id, op);
	}

	_render(){
		const snap = this.snapshot;
		const rowsEl = this.rowsEl;
		rowsEl.textContent = "";
		rowsEl.setAttribute("aria-label", this.labels.region || "Status");
		if(snap){
			if(snap.hidden && snap.hidden.count > 0){
				const chip = document.createElement("button");
				chip.type = "button";
				chip.className = "pvh-status-btn pvh-status-chip";
				chip.textContent = typeof this.labels.hidden === "function" ? this.labels.hidden(snap.hidden.count) : String(snap.hidden.count);
				chip.addEventListener("click", () => this._send("viewer", "restore"));
				rowsEl.appendChild(chip);
			}
			for(const row of snap.tasks || []) rowsEl.appendChild(this._row(row));
			if(snap.problem) rowsEl.appendChild(this._row(snap.problem));
			if(snap.notice) rowsEl.appendChild(this._row(snap.notice));
		}
		this._refresh();
	}

	_row(row){
		const el = document.createElement("div");
		el.className = "pvh-status-row pvh-status-row--" + (row.severity || "info");
		el.setAttribute("role", row.live === "assertive" ? "alert" : "status");
		el.setAttribute("aria-live", row.live || "polite");
		el.setAttribute("aria-atomic", "true");
		el.addEventListener("mouseenter", () => this._send(row.id, "pause"));
		el.addEventListener("mouseleave", () => this._send(row.id, "resume"));
		el.addEventListener("focusin", () => this._send(row.id, "pause"));
		el.addEventListener("focusout", () => this._send(row.id, "resume"));

		const icon = document.createElement("span");
		icon.setAttribute("aria-hidden", "true");
		if(row.state === "running"){
			icon.className = "pvh-spin";
		}else{
			icon.className = "pvh-status-icon";
			icon.textContent = GLYPH[row.state === "failed" ? "error" : (row.state === "done" ? "success" : (row.severity || "info"))] || GLYPH.info;
		}
		el.appendChild(icon);

		const body = document.createElement("div");
		body.className = "pvh-status-text";
		const text = document.createElement("span");
		text.textContent = row.text + (row.count > 1 ? " (" + row.count + ")" : "");
		body.appendChild(text);
		if(row.detail){
			const detail = document.createElement("span");
			detail.className = "pvh-status-detail";
			detail.setAttribute("aria-hidden", "true");
			detail.textContent = row.detail;
			body.appendChild(detail);
		}
		if(row.state === "running" && row.total > 0){
			const bar = document.createElement("div");
			bar.className = "pvh-status-bar";
			bar.setAttribute("aria-hidden", "true");
			const fill = document.createElement("i");
			fill.style.width = Math.max(0, Math.min(100, 100 * row.value / row.total)) + "%";
			bar.appendChild(fill);
			body.appendChild(bar);
		}
		el.appendChild(body);

		if(row.action){
			const button = document.createElement("button");
			button.type = "button";
			button.className = "pvh-status-btn";
			button.textContent = row.action.label;
			button.addEventListener("click", () => this._send(row.id, "action"));
			el.appendChild(button);
		}
		if(row.dismissable || row.closable){
			const x = document.createElement("button");
			x.type = "button";
			x.className = "pvh-status-x";
			x.setAttribute("aria-label", (row.dismissable ? this.labels.hide : this.labels.close) || "x");
			x.textContent = row.dismissable ? "–" : "×";
			x.addEventListener("click", () => this._send(row.id, row.dismissable ? "dismiss" : "close"));
			el.appendChild(x);
		}
		return el;
	}

	// pladsen vises, når den har indhold (nye rækker eller en synlig ældre besked)
	_hasContent(){
		if(this.rowsEl.childNodes.length > 0) return true;
		if(!this.legacy) return false;
		for(const m of this.legacy.querySelectorAll(".potree_message")){
			if(m.style.display !== "none") return true;
		}
		return false;
	}

	_refresh(){
		this.el.style.display = this._hasContent() ? "block" : "none";
		layoutHudStack();
	}

	_scheduleRefresh(){
		if(this.frame !== null) return;
		this.frame = requestAnimationFrame(() => {
			this.frame = null;
			this._refresh();
		});
	}

	dispose(){
		if(this.observer) this.observer.disconnect();
		if(this.frame !== null) cancelAnimationFrame(this.frame);
		window.removeEventListener("resize", this._onResize);
		unregisterHudSlot("status", this.el);
		if(this.el.parentNode) this.el.parentNode.removeChild(this.el);
	}
}
